import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import type { DuoPhotoRendererProps, PhotoDuoArrangement, PhotoDuoProps } from './PhotoDuo.types';
import { Box } from '@withjoy/joykit';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { pxToRem } from '@withjoy/joykit/theme';
import { parseAspectRatio } from '@shared/utils/parseAspectRatio';
import { OptimizedEventGuestSitePhoto, LOW_RES_RENDITION } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { addRendition } from '@shared/utils/photoRendition';

const ASPECT_RATIO = {
  primary: '7/5',
  secondary: '5/7',
};
const SCREEN_PROPORTION = {
  primary: '66.66%',
  secondary: '33.33%',
};

const SCREEN_PARTITION = {
  primary: 2 / 3,
  secondary: 1 / 3,
};

const GAP = 4;
const PHOTO_COUNT_TO_SHOW = 2;

const getAspectRatio = (arrangement: PhotoDuoArrangement, index: number) => {
  if (arrangement === 'left') {
    return index === 0 ? ASPECT_RATIO.primary : ASPECT_RATIO.secondary;
  }
  return index === 0 ? ASPECT_RATIO.secondary : ASPECT_RATIO.primary;
};

const getGridTemplateColumns = (arrangement: PhotoDuoArrangement) => {
  return arrangement === 'left' ? `${SCREEN_PROPORTION.primary} ${SCREEN_PROPORTION.secondary}` : `${SCREEN_PROPORTION.secondary} ${SCREEN_PROPORTION.primary}`;
};

const getScreenPartition = (arrangement: PhotoDuoArrangement, index: number) => {
  if (arrangement === 'left') {
    return index === 0 ? SCREEN_PARTITION.primary : SCREEN_PARTITION.secondary;
  }
  return index === 0 ? SCREEN_PARTITION.secondary : SCREEN_PARTITION.primary;
};

const getContainerHeight = (arrangement: PhotoDuoArrangement, index: number) => {
  if (arrangement === 'left') {
    return index === 0 ? '100%' : '100%';
  }
  return index === 0 ? 'auto' : '98%';
};

const DuoPhotoRenderer: FC<DuoPhotoRendererProps> = props => {
  const { photo, arrangement, index, containerDimensions, firstItemHeight } = props;
  const aspectRatio = getAspectRatio(arrangement, index);
  const screenPartition = getScreenPartition(arrangement, index);
  const width = containerDimensions.width * screenPartition;

  return (
    <Box
      width="100%"
      height={getContainerHeight(arrangement, index)}
      aspectRatio={aspectRatio}
    >
      <OptimizedEventGuestSitePhoto
        photoUrl={photo.photoUrl}
        cropInfo={photo.cropInfo ? { ...photo.cropInfo, unit: '%' } : null}
        width={width}
        height={firstItemHeight}
        containerType="fixed-aspect"
      />
    </Box>
  );
};

export const PhotoDuo: FC<PhotoDuoProps> = memo(props => {
  const { photos, arrangement, showSkeleton, showPlaceholder } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW);

  const [containerDimensions, containerRef] = useSizeObserver();

  // we will be using the height of first photo for rest of the photos to avoid height mismatch
  const firstItemHeight = useMemo(() => {
    if (photos.length === 0) {
      return 0;
    }
    const firstItemAspectRatio = getAspectRatio(arrangement, 0);
    return (containerDimensions.width * getScreenPartition(arrangement, 0)) / parseAspectRatio(firstItemAspectRatio);
  }, [arrangement, containerDimensions.width, photos.length]);

  if (!showPlaceholder && photos.length === 0) {
    return null;
  }

  return (
    <Box
      display="grid"
      ref={containerRef}
      gridTemplateColumns={{ _: getGridTemplateColumns(arrangement), xs: getGridTemplateColumns(arrangement) }}
      gap={pxToRem(GAP)}
      width="100vw"
      position="relative"
      left="calc(-50vw + 50%)"
      overflow="hidden"
    >
      {filledPhotos.map((photo, index) => (
        <SsrLazyScreen
          key={photo.id}
          src={addRendition({ url: photo.photoUrl, renditionSize: LOW_RES_RENDITION })}
          width="100%"
          height="100%"
          aspectRatio={getAspectRatio(arrangement, index)}
          showSkeleton={showSkeleton}
        >
          {() =>
            photo.photoUrl ? (
              <DuoPhotoRenderer
                photo={photo}
                arrangement={arrangement}
                index={index}
                containerDimensions={containerDimensions}
                firstItemHeight={firstItemHeight}
              />
            ) : (
              <Box
                width="100%"
                height="100%"
                maxHeight={firstItemHeight ? pxToRem(firstItemHeight) : 'unset'}
                aspectRatio={getAspectRatio(arrangement, index)}
                backgroundColor="lightgray"
                key={photo.id}
              />
            )
          }
        </SsrLazyScreen>
      ))}
    </Box>
  );
});

PhotoDuo.displayName = 'PhotoDuo';
